import { createContext, useContext, useMemo, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";

// Single production origin used for canonicals, OG URLs, and JSON-LD ids.
export const SITE_ORIGIN = "https://manitobaguards.ca";

const SeoDispatchContext = createContext(() => {});

export function useSeoDispatch() {
  return useContext(SeoDispatchContext);
}

// Central SEO dispatcher: every page reports its head tags via useSEO()
// (src/hooks/useSEO.js); this provider writes them into the document head
// through react-helmet-async. The static prerender (scripts/prerender.js)
// independently injects the same tags into each built HTML file, so crawlers
// see the correct title/description/canonical even before JavaScript runs.
export function SeoProvider({ children }) {
  const [seo, setSeo] = useState({});
  const dispatch = useMemo(() => setSeo, []);

  return (
    <SeoDispatchContext.Provider value={dispatch}>
      <HelmetProvider>
        <Helmet>
          {seo.title && <title>{seo.title}</title>}
          {seo.title && <meta property="og:title" content={seo.title} />}
          {seo.description && (
            <meta name="description" content={seo.description} />
          )}
          {seo.description && (
            <meta property="og:description" content={seo.description} />
          )}
          {seo.keywords && <meta name="keywords" content={seo.keywords} />}
          {seo.canonical && <link rel="canonical" href={seo.canonical} />}
          {seo.canonical && <meta property="og:url" content={seo.canonical} />}
          {seo.jsonLd && (
            <script type="application/ld+json">
              {JSON.stringify(seo.jsonLd)}
            </script>
          )}
        </Helmet>
        {children}
      </HelmetProvider>
    </SeoDispatchContext.Provider>
  );
}
